import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Import RTL CSS
import '../Assets/css/rtl.css';

// Import translation files
import enTranslations from '../locales/en.json';
import arTranslations from '../locales/ar.json';
import faTranslations from '../locales/fa.json';

const translations = {
  en: enTranslations,
  ar: arTranslations,
  fa: faTranslations,
};

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Helper function to get translation by path (e.g., 'menu.home' -> translations.menu.home)
const getNestedTranslation = (obj, path) => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract language from URL path (e.g., /en/, /ar/, /fa/)
  const getLanguageFromPath = (pathname) => {
    const match = pathname.match(/^\/(en|ar|fa)(\/|$)/);
    return match ? match[1] : null;
  };

  const [language, setLanguage] = useState(() => {
    // First try to get from URL
    const urlLanguage = getLanguageFromPath(location.pathname);
    if (urlLanguage) {
      return urlLanguage;
    }
    // Then try localStorage
    const savedLanguage = localStorage.getItem('imigo-language');
    if (savedLanguage && ['en', 'ar', 'fa'].includes(savedLanguage)) {
      return savedLanguage;
    }
    // Default to 'en'
    return 'en';
  });

  // Update language when URL changes
  useEffect(() => {
    const urlLanguage = getLanguageFromPath(location.pathname);
    if (urlLanguage && urlLanguage !== language) {
      setLanguage(urlLanguage);
      localStorage.setItem('imigo-language', urlLanguage);
      
      // Update document direction for RTL languages
      const isRTL = urlLanguage === 'ar' || urlLanguage === 'fa';
      document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
      document.documentElement.lang = urlLanguage;
      
      // Apply language-specific font family
      const root = document.documentElement;
      if (urlLanguage === 'ar') {
        root.style.setProperty('--font-primary', 'var(--font-arabic)');
      } else if (urlLanguage === 'fa') {
        root.style.setProperty('--font-primary', 'var(--font-farsi)');
      } else {
        root.style.setProperty('--font-primary', 'var(--font-english)');
      }
    }
  }, [location.pathname, language]);

  // Set initial document direction and font
  useEffect(() => {
    const isRTL = language === 'ar' || language === 'fa'
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr'
    document.documentElement.lang = language
    document.documentElement.classList.remove('lang-en', 'lang-ar', 'lang-fa')
    document.documentElement.classList.add(`lang-${language}`)
    
    // Apply language-specific font family
    const root = document.documentElement;
    if (language === 'ar') {
      root.style.setProperty('--font-primary', 'var(--font-arabic)');
    } else if (language === 'fa') {
      root.style.setProperty('--font-primary', 'var(--font-farsi)');
    } else {
      root.style.setProperty('--font-primary', 'var(--font-english)');
    }
  }, [language]);

  const changeLanguage = (newLanguage) => {
    if (!translations[newLanguage]) {
      console.warn(`Language ${newLanguage} not supported`);
      return;
    }

    setLanguage(newLanguage);
    localStorage.setItem('imigo-language', newLanguage);

    // Update document direction for RTL languages
    const isRTL = newLanguage === 'ar' || newLanguage === 'fa';
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = newLanguage;
    
    // Apply language-specific font family
    const root = document.documentElement;
    if (newLanguage === 'ar') {
      root.style.setProperty('--font-primary', 'var(--font-arabic)');
    } else if (newLanguage === 'fa') {
      root.style.setProperty('--font-primary', 'var(--font-farsi)');
    } else {
      root.style.setProperty('--font-primary', 'var(--font-english)');
    }

    // Update URL with new language prefix
    const pathWithoutLang = location.pathname.replace(/^\/(en|ar|fa)/, '');
    const newPath = `/${newLanguage}${pathWithoutLang || '/'}`;
    navigate(newPath, { replace: true });
  };

  const t = (key, params = {}) => {
    const translation = getNestedTranslation(translations[language], key);
    
    if (!translation) {
      console.warn(`Translation missing for key: ${key} in language: ${language}`);
      return key;
    }

    // Replace parameters in translation (e.g., {type} -> actual value)
    if (typeof translation === 'string' && Object.keys(params).length > 0) {
      return Object.keys(params).reduce((str, param) => {
        return str.replace(new RegExp(`\\{${param}\\}`, 'g'), params[param]);
      }, translation);
    }

    return translation;
  };

  const getCurrentPath = () => {
    // Remove language prefix from path
    return location.pathname.replace(/^\/(en|ar|fa)/, '') || '/';
  };

  const getLocalizedPath = (path) => {
    // Ensure path starts with /
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    // Remove any existing language prefix
    const pathWithoutLang = cleanPath.replace(/^\/(en|ar|fa)/, '');
    return `/${language}${pathWithoutLang}`;
  };

  const value = {
    language,
    changeLanguage,
    t,
    translations: translations[language],
    getCurrentPath,
    getLocalizedPath,
    supportedLanguages: ['en', 'ar', 'fa'],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;

